import React, { useState } from "react";
import { Check } from "lucide-react";

/**
 * Rocket-themed primary CTA. On click it plays a "launch" animation
 * (rocket flies up + smoke puff) before calling onClick().
 *
 * Variants:
 *  - launched=false → black launch button with a rocket icon
 *  - launched=true  → outlined "You're in" confirmation button
 */
export default function LaunchButton({
  onClick,
  launched = false,
  disabled = false,
  testId,
  size = "md",
  label = "Launch this",
  doneLabel = "You're in",
}) {
  const [animating, setAnimating] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (animating || disabled || launched || !onClick) return;
    setAnimating(true);
    try {
      // Fire the parent action immediately so vote count updates feel snappy.
      await onClick();
    } finally {
      // Keep the animation visible for its full duration regardless.
      setTimeout(() => setAnimating(false), 850);
    }
  };

  if (launched) {
    return (
      <button className="lp-launched-btn" disabled data-testid={testId ? `${testId}-done` : undefined}>
        <Check size={16} />
        <span>{doneLabel}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled || animating}
      className={`lp-launch-btn ${animating ? "is-launching" : ""}`}
      data-testid={testId}
      data-state={animating ? "launching" : "idle"}
    >
      <span className="smoke" aria-hidden />
      <RocketSvg />
      <span className="launch-label">{label}</span>
    </button>
  );
}

// Inline rocket SVG matching the Launchpad brand mark.
function RocketSvg() {
  return (
    <svg
      className="rocket"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 2c2.5 2.5 4 5.5 4 9 0 1.7-.4 3.3-1 4.7L12 18l-3-2.3c-.6-1.4-1-3-1-4.7 0-3.5 1.5-6.5 4-9Z" />
      <circle cx="12" cy="10" r="1.8" />
      <path d="M8 14l-3 2 1 4 4-2" />
      <path d="M16 14l3 2-1 4-4-2" />
      <path d="M10 19l2 3 2-3" />
    </svg>
  );
}
